import React, { memo, useEffect, useRef, useState } from 'react';
import styles from './Iframe.module.css';
import { formatVideoUrlForIframe, getYoutubeThumbnail } from '../urlUtils';
import { useLocation } from 'react-router-dom';
import TwitterEmbed from './TwitterEmbed';
import { cx } from '#/helpers/css';

interface IframeProps {
  url?: URL | undefined;
  embed_html?: string;
}

const Iframe = ({
  url,
  embed_html
}: IframeProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const iframeHeightRef = useRef<number>(0);
  const iframeWidthRef = useRef<number>(0);
  const location = useLocation();
  const iframeRef = useRef<HTMLDivElement | null>(null);
  const adjustedIframe = useRef<string>(embed_html ?? "");
  const youtubeThumbnailUrl = url?.href ? getYoutubeThumbnail(url.href) : null;

  const isYoutubeUrl = url && url.origin && url.origin.includes('youtube.com');
  const hasTwitterEmbed = embed_html && embed_html.includes(`twitter-tweet`);

  useEffect(() => {
    if (location.pathname.includes('/comments')) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [location]);

  useEffect(() => {
    const adjustIframeDimensions = () => {
      const isMobile = window.innerWidth < 480;
      if (!isMobile || !embed_html || hasTwitterEmbed) {
        return;
      }

      // Extract width & height using regex
      const widthMatch = embed_html.match(/width="(\d+)"/);
      const heightMatch = embed_html.match(/height="(\d+)"/);
      const originalWidth = widthMatch?.[1] ? parseInt(widthMatch[1]) : 600;
      const originalHeight = heightMatch?.[1] ? parseInt(heightMatch[1]) : 400;
      
      const newWidth = Math.min(window.innerWidth * 0.9, 400);
      const aspectRatio = originalHeight / originalWidth < 1
        ? originalHeight / originalWidth + 1
        : originalHeight / originalWidth;
      let newHeight;
      if (originalHeight > 500) {
        newHeight = originalHeight + 50;
      } else {
        newHeight = newWidth + aspectRatio * 100;
      }
      iframeHeightRef.current = newHeight;
      iframeWidthRef.current = newWidth;

      adjustedIframe.current = embed_html
        .replace(/width="\d+"/, `width="${newWidth}"`)
        .replace(/height="\d+"/, `height="${Math.round(newHeight)}"`);
        
      if (iframeRef.current) {
        iframeRef.current.innerHTML = adjustedIframe.current;
      }
      
    };

    adjustIframeDimensions();
    // window.addEventListener('resize', adjustIframeDimensions);

    // return () => {
    //   window.removeEventListener('resize', adjustIframeDimensions);
    // };
  }, [embed_html]);

  if (hasTwitterEmbed) {
    return (
      <div style={{position: 'relative', height: '100%', width: '100%'}}>
        {!isPlaying
          ? <div className={cx(styles.iframeInFeed, styles.twitterIframe)} />
          : null
        }
        <TwitterEmbed
          embed_html={embed_html}
        />
      </div>
    )
  }

  if (isYoutubeUrl) {
    return (
      <div
        className={cx(
          styles.ytIframe,
          url.pathname?.includes('shorts') ? styles.ytShortsIframe : '',
        )}
        onClick={() => setIsPlaying(true)}
      >
        {!isPlaying ? (
          <>
            <img
              src={youtubeThumbnailUrl || ""}
            />
            <svg
              viewBox="0 0 68 48"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "84px",
                height: "60px",
                cursor: "pointer"
              }}
            >
              <path
                d="M66.52 7.01a8.27 8.27 0 00-5.83-5.84C56.45 0 34 0 34 0S11.55 0 7.31 1.17a8.27 8.27 0 00-5.83 5.84C0.3 11.26 0 17.09 0 24s.3 12.74 1.48 16.99a8.27 8.27 0 005.83 5.84C11.55 48 34 48 34 48s22.45 0 26.69-1.17a8.27 8.27 0 005.83-5.84C67.7 36.74 68 30.91 68 24s-.3-12.74-1.48-16.99z"
                fill="#f00"
              />
              <path d="M45 24L27 14v20z" fill="#fff" />
            </svg>            
          </>
        ) : (
          <iframe
            src={formatVideoUrlForIframe(url, false)}
            width="100%"
            height="100%"
            allow="autoplay; fullscreen"
            allowFullScreen
          />
        )}
      </div>
    )
  }

  if (adjustedIframe.current && adjustedIframe.current?.includes('iframe')) {
    return (
      <div style={{position: 'relative', height: '100%', width: '100%'}}>
        {!isPlaying
          ? <div className={styles.iframeInFeed} />
          : null
        }
        <div
          ref={iframeRef}
          className={styles.iframe}
          dangerouslySetInnerHTML={{ __html: adjustedIframe.current }}
        />
      </div>
    )
  }

  if (adjustedIframe.current) {
    return (
      <div
        ref={iframeRef}
        className={styles.iframe}
        dangerouslySetInnerHTML={{ __html: adjustedIframe.current }}
      />
    )
  }
}

export default memo(Iframe);